/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: TenantSelect.tsx
 🗓️ Created: 2025-01-06
 🧩 Description:
    Custom tenant selection component with search functionality,
    limited display options, and keyboard navigation support.

 🔧 Notes:
    - Implements searchable dropdown with 5-item display limit
    - Supports keyboard navigation (arrow keys, enter, escape)
    - Includes tenant options with telephony ID mapping
    - Optimized for performance with filtered results
    - Integrates with form validation systems

 🔁 Last Updated: 2025-01-06 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import React, { useState, useMemo, useCallback, memo } from 'react';
import { ChevronDown, Search, Building2 } from 'lucide-react';

interface Tenant {
  id: string;
  name: string;
  slug: string;
  telephonyId: number;
}

interface TenantSelectProps {
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  tenants: Tenant[];
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
}

export const TenantSelect: React.FC<TenantSelectProps> = ({
  value,
  onChange,
  tenants,
  className = '',
  placeholder = 'Select tenant...',
  disabled = false,
  error = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Filtrar opciones basado en la búsqueda
  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) {
      return tenants;
    }
    
    const query = searchQuery.toLowerCase();
    return tenants.filter(tenant => 
      tenant.name.toLowerCase().includes(query) ||
      tenant.slug.toLowerCase().includes(query)
    );
  }, [tenants, searchQuery]);

  // Obtener el tenant seleccionado
  const selectedTenant = tenants.find(tenant => tenant.telephonyId === value);

  // Manejar selección
  const handleSelect = (tenant: Tenant) => {
    onChange(tenant.telephonyId);
    setIsOpen(false);
    setSearchQuery('');
  };

  // Manejar teclas
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        }
        break;
      case 'Enter':
        e.preventDefault();
        if (isOpen && filteredOptions.length > 0) {
          handleSelect(filteredOptions[0]);
        } else {
          setIsOpen(!isOpen);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearchQuery('');
        break;
    }
  };

  return (
    <div className="relative">
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className={`
          w-full px-3 py-2 text-sm border rounded bg-background text-foreground 
          focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent
          flex items-center justify-between gap-2
          ${error ? 'border-red-500' : 'border-border'}
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-muted/50'}
          ${className}
        `}
      >
        <div className="flex items-center gap-2 min-w-0">
          <Building2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <span className="truncate">
            {selectedTenant ? selectedTenant.name : placeholder}
          </span>
        </div>
        <ChevronDown 
          className={`h-4 w-4 text-muted-foreground transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`} 
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-background border border-border rounded-md shadow-lg">
          {/* Search Input */}
          <div className="p-2 border-b border-border">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search tenants..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
                autoFocus
              />
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-60 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-muted-foreground text-center">
                {searchQuery ? 'No tenants found' : 'No tenants available'}
              </div>
            ) : (
              filteredOptions.slice(0, 5).map((tenant) => (
                <button
                  key={tenant.id}
                  type="button"
                  onClick={() => handleSelect(tenant)}
                  className={`
                    w-full px-3 py-2 text-sm text-left hover:bg-muted/50 focus:bg-muted/50 focus:outline-none
                    flex items-center gap-2
                    ${tenant.telephonyId === value ? 'bg-primary/10 text-primary' : 'text-foreground'}
                  `}
                >
                  <Building2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{tenant.name}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {tenant.slug} (ID: {tenant.telephonyId})
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantSelect;
